import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Hook para controle de acesso baseado em status de validação e assinatura
export const useAccessControl = () => {
  const { usuario, empresa } = useAuth();
  
  const accessStatus = useMemo(() => {
    // Admins têm acesso total
    if (usuario?.perfil === 'master_admin' || usuario?.perfil === 'super_admin') {
      return {
        hasFullAccess: true,
        isApproved: true,
        hasActiveSubscription: true,
        blockReason: null,
        redirectTo: null,
      };
    }
    
    // Funcionários herdam o status da empresa do entregador
    // Verificação para clientes e entregadores
    const isApproved = empresa?.status_validacao === 'aprovado';
    const hasActiveSubscription = empresa?.assinatura_ativa === true;
    
    let blockReason = null;
    let redirectTo = null;
    
    if (!isApproved) {
      const status = empresa?.status_validacao || 'cadastro_iniciado';
      switch (status) {
        case 'cadastro_iniciado':
          blockReason = 'complete_registration';
          redirectTo = `documentos`;
          break;
        case 'documentos_enviados':
        case 'em_analise':
          blockReason = 'pending_approval';
          redirectTo = null; // Aguardar aprovação
          break;
        case 'rejeitado':
          blockReason = 'rejected';
          redirectTo = `documentos`;
          break;
        case 'bloqueado':
          blockReason = 'blocked';
          redirectTo = null;
          break;
        default:
          blockReason = 'unknown';
          redirectTo = null;
      }
    } else if (!hasActiveSubscription) {
      blockReason = 'no_subscription';
      redirectTo = `financeiro`;
    }
    
    return {
      hasFullAccess: isApproved && hasActiveSubscription,
      isApproved,
      hasActiveSubscription,
      blockReason,
      redirectTo,
    };
  }, [usuario, empresa]);
  
  return accessStatus;
};

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Carrega dados do localStorage
    const usuarioSalvo = localStorage.getItem('usuario');
    const empresaSalva = localStorage.getItem('empresa');
    
    if (usuarioSalvo && empresaSalva) {
      setUsuario(JSON.parse(usuarioSalvo));
      setEmpresa(JSON.parse(empresaSalva));
    }
    
    setLoading(false);
  }, []);
  
  const login = (usuarioData, empresaData) => {
    setUsuario(usuarioData);
    setEmpresa(empresaData);
    localStorage.setItem('usuario', JSON.stringify(usuarioData));
    localStorage.setItem('empresa', JSON.stringify(empresaData));
  };
  
  const logout = () => {
    setUsuario(null);
    setEmpresa(null);
    localStorage.removeItem('usuario');
    localStorage.removeItem('empresa');
    localStorage.removeItem('rota017_token');
  };
  
  const atualizarEmpresa = (empresaData) => {
    setEmpresa(empresaData);
    localStorage.setItem('empresa', JSON.stringify(empresaData));
  };
  
  return (
    <AuthContext.Provider value={{ usuario, empresa, login, logout, atualizarEmpresa, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
